import type { ClientOptions } from "ably";

type AblyToxiproxyOverrides = Pick<
	ClientOptions,
	"endpoint" | "port" | "tls" | "tlsPort" | "disableConnectivityCheck" | "fallbackHosts"
>;

function parseNumber(value: string | undefined): number | undefined {
	if (value === undefined || value === "") return undefined;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : undefined;
}

function parseBoolean(value: string | undefined): boolean | undefined {
	if (value === "true") return true;
	if (value === "false") return false;
	return undefined;
}

function isToxiproxyEnabled(): boolean {
	return import.meta.env.VITE_ABLY_TOXIPROXY_ENABLED === "true";
}

function getEnabledClientIds(): string[] {
	return ((import.meta.env.VITE_ABLY_TOXIPROXY_CLIENT_IDS as string | undefined) ?? "")
		.split(",")
		.map((value) => value.trim())
		.filter(Boolean);
}

export function getAblyToxiproxyOverrides(clientId?: string): AblyToxiproxyOverrides {
	if (!isToxiproxyEnabled()) {
		return {};
	}

	const enabledClientIds = getEnabledClientIds();

	if (enabledClientIds.length > 0 && (!clientId || !enabledClientIds.includes(clientId))) {
		return {};
	}

	const endpoint = (import.meta.env.VITE_ABLY_TOXIPROXY_ENDPOINT as string | undefined)?.trim() || undefined;
	const port = parseNumber(import.meta.env.VITE_ABLY_TOXIPROXY_PORT as string | undefined);
	const tls = parseBoolean(import.meta.env.VITE_ABLY_TOXIPROXY_TLS as string | undefined);
	const tlsPort = parseNumber(import.meta.env.VITE_ABLY_TOXIPROXY_TLS_PORT as string | undefined);
	const disableConnectivityCheck = parseBoolean(
		import.meta.env.VITE_ABLY_TOXIPROXY_DISABLE_CONNECTIVITY_CHECK as string | undefined,
	);

	return {
		...(endpoint ? { endpoint } : {}),
		...(typeof port === "number" ? { port } : {}),
		...(typeof tls === "boolean" ? { tls } : {}),
		...(typeof tlsPort === "number" ? { tlsPort } : {}),
		...(typeof disableConnectivityCheck === "boolean" ? { disableConnectivityCheck } : {}),
		// Prevent the Ably SDK from falling back to *.ably-realtime.com fallback hosts
		// which are not covered by /etc/hosts and would bypass the proxy entirely.
		fallbackHosts: [],
	};
}
